'use client';

import { useState } from 'react';
import { Sparkles, Copy, RefreshCw } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { youtubeTitleGeneratorContent } from '../../data/youtube-title-generator-content';

export default function YoutubeTitleGenerator() {
    const [topic, setTopic] = useState('');
    const [tone, setTone] = useState('engaging');
    const [titles, setTitles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const tones = [
        { value: 'engaging', label: 'Engaging' },
        { value: 'clickbait', label: 'Clickbait' },
        { value: 'professional', label: 'Professional' },
        { value: 'funny', label: 'Funny' },
        { value: 'dramatic', label: 'Dramatic' },
    ];

    const generateTitles = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!topic.trim()) {
            setError('Please enter a topic');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch('/api/generate/youtube-titles', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ topic, tone }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate titles');
            }

            const data = await response.json();
            setTitles(data.titles);
        } catch (err) {
            console.error('Error generating titles:', err);
            setError('Failed to generate titles. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const regenerateTitle = async (index: number) => {
        if (!topic.trim()) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch('/api/generate/youtube-titles', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    topic,
                    tone,
                    generateSingle: true
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to regenerate title');
            }

            const data = await response.json();
            const newTitles = [...titles];
            newTitles[index] = data.titles[0];
            setTitles(newTitles);
        } catch (err) {
            console.error('Error regenerating title:', err);
            setError('Failed to regenerate title. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="YouTube Title Generator - Free Online Video Title Creator">
                Generate catchy, SEO-optimized YouTube titles with our free online tool. Boost your video rankings and attract more viewers.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <form onSubmit={generateTitles} className="space-y-6">
                    <div>
                        <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                            Video Topic
                        </label>
                        <input
                            type="text"
                            id="topic"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="e.g., How to make perfect pancakes"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                            Tone
                        </label>
                        <select
                            id="tone"
                            value={tone}
                            onChange={(e) => setTone(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {tones.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full px-6 py-3 rounded-lg flex items-center justify-center ${isLoading
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                } text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                        >
                            {isLoading ? (
                                <>
                                    <RefreshCw className="animate-spin h-5 w-5 mr-2" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-5 w-5 mr-2" />
                                    Generate Titles
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {titles.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        Generated Titles
                    </h2>
                    <div className="space-y-4">
                        {titles.map((title, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                                <span className="text-gray-800">{title}</span>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => copyToClipboard(title)}
                                        className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                                        title="Copy to clipboard"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => regenerateTitle(index)}
                                        disabled={isLoading}
                                        className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
                                        title="Regenerate"
                                    >
                                        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Pro Tip</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                            <p>For best results, be specific about your video content and target audience in the topic field.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={youtubeTitleGeneratorContent.title}
                    description={youtubeTitleGeneratorContent.description}
                    content={youtubeTitleGeneratorContent.content}
                    faqs={youtubeTitleGeneratorContent.faqs}
                    relatedTools={youtubeTitleGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}
